import { useContext, useEffect, useState } from 'react';
import { HighchartsReactRefObject } from 'highcharts-react-official';

import AccountContext from 'contexts/AccountContext';
import DashboardContextMenuContext from 'contexts/DashboardContextMenuContext';
import useV5ChartData from 'hooks/useV5ChartData';
import useChartDrillDowns from 'hooks/useChartDrillDowns';
import useMetric from 'hooks/useMetric';
import useValueFormatters from 'hooks/useValueFormatters';
import metricTypeCheckers from 'types/metricTypeCheckers';
import funnel from '../highchartOptions/funnelOptions';
import toBreakdownRows from '../toBreakdownRows';

interface FunnelSeriesItem {
  name: string;
  data: { name: string; y: number }[];
  metricId: string;
  metricIds: string[];
}

// Reuses the single-metric breakdown data (one metric grouped by dimensionA /
// date / day-of-week) and maps each grouping to a {name, y} funnel band.
export const getSeriesFor = (
  data: V5ChartData,
  chartDef: V5ChartDefinition,
): FunnelSeriesItem[] => {
  const firstOption = Object.values(data)[0];
  if (!firstOption) {
    return [];
  }
  const metric = firstOption.metric;
  const metricId = metric.id;
  const metricIds = metricTypeCheckers.isCompoundMetric(metric)
    ? metric.metricIds
    : [metric.id];

  // Funnel/pyramid read top-to-bottom largest-to-smallest.
  const rows = toBreakdownRows({ data, chartDef })
    .map((row) => ({ name: row.name, y: row.value }))
    .sort((a, b) => b.y - a.y);
  if (rows.length === 0) {
    return [];
  }

  return [
    {
      name: metric.name,
      metricId,
      metricIds,
      data: rows,
    },
  ];
};

const useFunnelData = ({
  chartDefinition,
  chartRef,
}: {
  chartDefinition: V5ChartDefinition;
  chartRef: React.MutableRefObject<HighchartsReactRefObject | null>;
}): {
  options: Highcharts.Options | undefined;
  isNoData: boolean;
  isReady: boolean;
} => {
  const { unitsLocale } = useContext(AccountContext);
  const { openMenu } = useContext(DashboardContextMenuContext);

  const [groupField] = useState(() =>
    chartDefinition.dimensionA ? chartDefinition.dimensionA.field : 'date',
  );
  const [options, setOptions] = useState<Highcharts.Options | undefined>();
  const [isNoDataToDisplay, setIsNoDataToDisplay] = useState<boolean>(false);

  const { data, isLoading } = useV5ChartData(chartDefinition);
  const { formatMetric } = useValueFormatters();
  const metric = useMetric(chartDefinition.series[0].metricId);
  const { onDrillDown, syncChart } = useChartDrillDowns(
    groupField,
    chartRef,
    chartDefinition.gadgetType,
    chartDefinition,
  );

  useEffect(() => {
    if (!metric) {
      return;
    }

    setOptions(
      funnel({
        formatMetric,
        title: ' ',
        openMenu,
        groupField,
        fieldType: chartDefinition.dimensionA
          ? chartDefinition.dimensionA.fieldType
          : undefined,
        chartDef: chartDefinition,
        fontSize: 11,
        type: chartDefinition.gadgetType === 'pyramid' ? 'pyramid' : 'funnel',
      }) as any as Highcharts.Options,
    );
  }, [
    chartDefinition,
    formatMetric,
    groupField,
    metric,
    openMenu,
    unitsLocale,
  ]);

  useEffect(() => {
    if (!data) {
      return;
    }

    const series = getSeriesFor(data, chartDefinition).map((s) => ({
      ...s,
      type: (chartDefinition.gadgetType === 'pyramid'
        ? 'pyramid'
        : 'funnel') as 'funnel' | 'pyramid',
      onDrillDown,
    }));
    const isNoData =
      series.length === 0 ||
      series[0].data.length === 0 ||
      !series[0].data.some((d) => !isNaN(d.y));

    setIsNoDataToDisplay(isNoData);
    setOptions((current) => ({
      ...current,
      series,
    }));
  }, [chartDefinition, data, onDrillDown]);

  useEffect(() => {
    syncChart();
  }, [syncChart, onDrillDown, data]);

  return {
    options,
    isNoData: isNoDataToDisplay,
    isReady: !!data && !!metric && !isLoading,
  };
};

export default useFunnelData;
